import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Scissors, ArrowLeft, Check, Sparkles, Heart } from "lucide-react";

/**
 * Public referral page: /join/:slug
 * - Anyone: sees the groomer's business, services and can sign up as a client.
 * - Logged-in client: one-tap "join this groomer" button re-assigns their groomer_id.
 * - Logged-in groomer: gets a friendly nudge back to the dashboard.
 */
export default function JoinBySlug() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, setSessionUser } = useAuth();
  const [groomer, setGroomer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", address: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/public/groomer/by-slug/${slug}`)
      .then(({ data }) => setGroomer(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const alreadyJoined = user?.role === "client" && groomer && user.groomer_id === groomer.id;

  const submitSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/join/${slug}/register`, form);
      localStorage.setItem("pawtrek_token", data.token);
      localStorage.setItem("pawtrek_user", JSON.stringify(data.user));
      setSessionUser(data.user);
      toast.success(`You're linked to ${data.groomer.business_name}!`);
      nav("/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  const joinExisting = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/join/${slug}`);
      localStorage.setItem("pawtrek_user", JSON.stringify(data.user));
      setSessionUser(data.user);
      toast.success(`Linked to ${data.groomer.business_name}`);
      nav("/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join this groomer");
    } finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] grid place-content-center px-6 text-center">
        <div className="max-w-md space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-[#F5E6CF] grid place-content-center mx-auto text-[#C85A17]">
            <Scissors className="w-6 h-6"/>
          </div>
          <h1 className="font-display text-3xl font-bold">Invite link not found</h1>
          <p className="text-sm text-[#6B625B]">
            This invite may have been mistyped or the groomer has changed their link.
          </p>
          <Link to="/" className="inline-block pill bg-[#C85A17] text-white mt-2" data-testid="join-back-home">
            <ArrowLeft className="w-3 h-3"/> Back home
          </Link>
        </div>
      </div>
    );
  }

  if (!groomer) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] grid place-content-center text-sm text-[#6B625B]">
        Loading invite…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="max-w-7xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1F1A17]">
          <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white">
            <Scissors className="w-4 h-4"/>
          </div>
          <span className="font-display text-xl font-bold">PawTrek</span>
        </Link>
        <Link to="/" className="text-sm text-[#6B625B] flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Home</Link>
      </header>

      <main className="flex-1 grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto px-6 pb-12 w-full">
        {/* Left: groomer profile */}
        <div className="space-y-4 self-center" data-testid="join-groomer-card">
          <div className="pill bg-[#F5E6CF] text-[#A9480F] border border-amber-900/10 w-fit">
            <Sparkles className="w-3 h-3"/> You're invited
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.05]">
            Join <span className="text-[#C85A17]">{groomer.business_name}</span> on PawTrek
          </h1>
          <p className="text-[#6B625B] max-w-md">
            {groomer.name} uses PawTrek to schedule mobile grooming, message clients and
            keep their books in one place. Sign up as a client to book with them directly.
          </p>

          {groomer.services?.length > 0 && (
            <div className="bg-white border border-[#E5DCCE] rounded-3xl p-5 space-y-2">
              <div className="label-accent text-[#A9480F]">Services</div>
              <ul className="divide-y divide-[#E5DCCE]" data-testid="join-services-list">
                {groomer.services.map((s) => (
                  <li key={s.name} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-[#6B625B]">${s.price} · {s.duration_min}m</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: action panel */}
        <div className="self-center">
          {user?.role === "groomer" || user?.role === "super_admin" ? (
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-8 space-y-3 text-center">
              <h2 className="font-display text-2xl font-bold">You're a groomer 👋</h2>
              <p className="text-sm text-[#6B625B]">
                This invite link is meant for clients. Head back to your dashboard.
              </p>
              <Button onClick={() => nav("/groomer")}
                className="rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="join-goto-dashboard">
                Go to dashboard
              </Button>
            </div>
          ) : alreadyJoined ? (
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-8 space-y-3 text-center">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 grid place-content-center mx-auto">
                <Check className="w-6 h-6"/>
              </div>
              <h2 className="font-display text-2xl font-bold">You're already linked</h2>
              <p className="text-sm text-[#6B625B]">
                {groomer.business_name} is your groomer. Book your next appointment now.
              </p>
              <Button onClick={() => nav("/client/book")}
                className="rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="join-book-now">
                Book an appointment
              </Button>
            </div>
          ) : user?.role === "client" ? (
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-8 space-y-4 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#F5E6CF] text-[#A9480F] grid place-content-center mx-auto">
                <Heart className="w-6 h-6"/>
              </div>
              <h2 className="font-display text-2xl font-bold">Switch to {groomer.name}?</h2>
              <p className="text-sm text-[#6B625B]">
                Signed in as <span className="font-semibold">{user.email}</span>.
                Tap below and your PawTrek account will be linked to {groomer.business_name}.
              </p>
              <Button onClick={joinExisting} disabled={busy}
                className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12"
                data-testid="join-existing-btn">
                {busy ? "Joining…" : `Join ${groomer.business_name}`}
              </Button>
            </div>
          ) : (
            <form onSubmit={submitSignup}
              className="bg-white border border-[#E5DCCE] rounded-3xl p-8 shadow-sm space-y-3"
              data-testid="join-signup-form">
              <h2 className="font-display text-2xl font-bold">Sign up as a client</h2>
              <p className="text-sm text-[#6B625B]">You'll be linked to {groomer.business_name} automatically.</p>
              <div><Label>Full name</Label>
                <Input required value={form.name} onChange={set("name")} data-testid="join-name"/></div>
              <div><Label>Email</Label>
                <Input type="email" required value={form.email} onChange={set("email")} data-testid="join-email"/></div>
              <div><Label>Password</Label>
                <Input type="password" required minLength={6} value={form.password}
                  onChange={set("password")} data-testid="join-password"/></div>
              <div><Label>Phone</Label>
                <Input value={form.phone} onChange={set("phone")} data-testid="join-phone"/></div>
              <div><Label>Address</Label>
                <Input value={form.address} onChange={set("address")}
                  placeholder="Where should the van meet you?" data-testid="join-address"/></div>
              <Button type="submit" disabled={busy}
                className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12 mt-2"
                data-testid="join-submit">
                {busy ? "Creating account…" : `Join ${groomer.business_name}`}
              </Button>
              <p className="text-[11px] text-center text-[#6B625B] pt-1">
                Already have an account?{" "}
                <Link to={`/login?next=/join/${slug}`} className="text-[#C85A17] font-semibold"
                  data-testid="join-signin-link">
                  Sign in
                </Link>{" "}
                and open this link again to link to {groomer.name}.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
